import { useEffect } from 'react';
import PropTypes from 'prop-types';
import Container from 'react-bootstrap/Container';
import Row from 'react-bootstrap/Row';
import Col from 'react-bootstrap/Col';
import { IoCalendar } from 'react-icons/io5';
import {
  LuArrowRight,
  LuCalendar,
  LuCopy,
  LuMoreVertical,
  LuPencil,
  LuTrash2,
  LuUsers,
} from 'react-icons/lu';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { useHistory } from 'react-router-dom';

import { isFeatureEnabled } from 'helpers';
import { LoadingIcon } from '_components/_shared';
import { PageHeader, Button, Dropdown } from '_components/_core';

import {
  EventCard,
  EventTitle,
  EventDescription,
  EventDetail,
  EventStatusBadge,
} from './styles';

function Events({ events, isLoading, onFetchEvents, onCreateEvent, onDeleteEvent, user }) {
  const history = useHistory();

  useEffect(() => {
    onFetchEvents();
  }, [onFetchEvents]);

  const handleCreateEvent = () => {
    history.push('/eventos/novo');
  };

  const handleEditEvent = event => {
    history.push(`/eventos/${event._id}/editar`);
  };

  const formatDateRange = (startDate, endDate) => {
    const start = new Date(startDate);
    const end = new Date(endDate);

    // If same year and month
    if (
      start.getFullYear() === end.getFullYear() &&
      start.getMonth() === end.getMonth()
    ) {
      return `${format(start, 'dd', { locale: ptBR })} - ${format(end, "dd 'de' MMMM, yyyy", { locale: ptBR })}`;
    }

    // If same year but different month
    if (start.getFullYear() === end.getFullYear()) {
      return `${format(start, "dd 'de' MMMM", { locale: ptBR })} - ${format(end, "dd 'de' MMMM, yyyy", { locale: ptBR })}`;
    }

    // Different years
    return `${format(start, "dd 'de' MMMM, yyyy", { locale: ptBR })} - ${format(end, "dd 'de' MMMM, yyyy", { locale: ptBR })}`;
  };

  const handleDuplicateEvent = event => {
    onCreateEvent(event);
  };

  const handleDeleteEvent = event => {
    onDeleteEvent(event._id);
  };

  if (!isFeatureEnabled('events', user.flags)) {
    return null;
  }

  return (
    <Container className="content-wrapper">
      <PageHeader
        title="Eventos"
        description="Gerencie seus eventos e inscrições"
        className="ml-3"
        variant="secondary"
        sideContent={
          <Button
            variant="white"
            className="d-flex align-items-center"
            onClick={handleCreateEvent}
          >
            <IoCalendar className="mr-2 text-success" />
            Novo Evento
          </Button>
        }
      />
      {isLoading && (
        <Row>
          <Col>
            <LoadingIcon text="Carregando eventos..." />
          </Col>
        </Row>
      )}
      {!isLoading && (
        <Row>
          {events?.data?.map(event => (
            <Col md={6} lg={4} key={event._id} className="mb-4">
              <EventCard>
                <EventTitle className="d-flex align-items-center justify-content-between">
                  {event.name}
                  <div className="d-flex align-items-center justify-content-end">
                    <Dropdown
                      placement="bottom-start"
                      options={[
                        {
                          label: 'Editar',
                          icon: <LuPencil />,
                          onClick: () => handleEditEvent(event),
                          disabled: false,
                        },
                        {
                          label: 'Duplicar',
                          icon: <LuCopy />,
                          onClick: () => handleDuplicateEvent(event),
                          disabled: false,
                        },
                        {
                          label: 'Excluir',
                          icon: <LuTrash2 className="text-danger" />,
                          onClick: () => handleDeleteEvent(event),
                        },
                      ]}
                    >
                      <Button
                        variant="white"
                        className="d-flex align-items-center justify-content-center m-0 p-0 p-2"
                      >
                        <LuMoreVertical
                          style={{
                            cursor: 'pointer',
                          }}
                        />
                      </Button>
                    </Dropdown>
                  </div>
                </EventTitle>
                <EventDescription>{event.address}</EventDescription>

                <EventDetail>
                  <LuCalendar />
                  {formatDateRange(event.start_date, event.end_date)}
                </EventDetail>

                <EventDetail>
                  <LuUsers />
                  Capacidade: {event.capacity}
                </EventDetail>
                <hr />
                <div className="d-flex justify-content-between mt-3">
                  <div className="d-flex justify-content-start align-items-center">
                    <div>
                      <EventStatusBadge published={true} />
                      <small>Inscrições abertas</small>
                    </div>
                  </div>
                  <Button
                    variant="white"
                    size="sm"
                    onClick={() => history.push(`/eventos/${event._id}/gerenciar`)}
                    className="d-flex align-items-center"
                  >
                    Gerenciar <LuArrowRight className="ml-1" />
                  </Button>
                </div>
              </EventCard>
            </Col>
          ))}
        </Row>
      )}
    </Container>
  );
}

Events.propTypes = {
  events: PropTypes.shape({
    data: PropTypes.array,
    page: PropTypes.number,
    limit: PropTypes.number,
    total: PropTypes.number,
  }),
  isLoading: PropTypes.bool.isRequired,
  onFetchEvents: PropTypes.func.isRequired,
  onCreateEvent: PropTypes.func.isRequired,
  onUpdateEvent: PropTypes.func.isRequired,
};

Events.defaultProps = {
  events: {
    data: [],
    page: 1,
    limit: 10,
    total: 0,
  },
};

export default Events;
